const GA_SCRIPT_ID = 'ga4-script'
const US_MEASUREMENT_ID = 'G-CC2VHP6Z3P'
const IN_MEASUREMENT_ID = 'G-N17EKRN0MM'

let initialized = false
let measurementId: string | undefined
let lastTrackedSafePagePath: string | undefined

const NON_LIVE_HOST_SEGMENT =
  /(^|[.-])(dev|uat|test|staging|beta|qa|preview)(?=[.-]|$)/

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export function shouldEnableGoogleAnalytics(hostname?: string): boolean {
  if (typeof window === 'undefined' && !hostname) return false

  const currentHostname = (hostname ?? window.location.hostname).toLowerCase()

  if (
    currentHostname === 'localhost' ||
    currentHostname === '127.0.0.1' ||
    currentHostname === '0.0.0.0' ||
    currentHostname.endsWith('.local')
  ) {
    return false
  }

  if (NON_LIVE_HOST_SEGMENT.test(currentHostname)) {
    return false
  }

  return (
    currentHostname === 'firstinmath.com' ||
    currentHostname === 'firstinmath.in' ||
    currentHostname.endsWith('.firstinmath.com') ||
    currentHostname.endsWith('.firstinmath.in')
  )
}

export function getGoogleAnalyticsMeasurementId(hostname?: string): string {
  if (typeof window === 'undefined' && !hostname) return US_MEASUREMENT_ID

  const currentHostname = (hostname ?? window.location.hostname).toLowerCase()
  return currentHostname.endsWith('.in') ? IN_MEASUREMENT_ID : US_MEASUREMENT_ID
}

export function initGoogleAnalytics(hostname?: string): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  if (!shouldEnableGoogleAnalytics(hostname)) {
    return false
  }

  if (initialized) {
    return true
  }

  measurementId = getGoogleAnalyticsMeasurementId(hostname)

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function () {
      // Match Google's canonical bootstrap command shape.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = GA_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script)
  }

  window.gtag('js', new Date())
  // The GA streams must have Enhanced Measurement history events disabled.
  // Otherwise GA also tracks SPA navigations and duplicates the manual events.
  window.gtag('config', measurementId, {
    send_page_view: false,
    page_location: `${window.location.origin}${getGoogleAnalyticsPagePath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    )}`
  })

  initialized = true
  return true
}

export function trackGoogleAnalyticsPageView(path?: string): void {
  if (
    typeof window === 'undefined' ||
    !initialized ||
    !measurementId ||
    typeof window.gtag !== 'function'
  ) {
    return
  }

  const pagePath =
    path ??
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  const safePagePath = getGoogleAnalyticsPagePath(pagePath)

  if (lastTrackedSafePagePath === safePagePath) {
    return
  }

  window.gtag('event', 'page_view', {
    page_path: safePagePath,
    page_location: `${window.location.origin}${safePagePath}`
  })

  lastTrackedSafePagePath = safePagePath
}

export function getGoogleAnalyticsPagePath(path: string): string {
  return path.split(/[?#]/, 1)[0] || '/'
}
